import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sdrive_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Phase 1 stability — debounce the post-401 redirect.
//
// Background: on iPhone the JS execution context is frozen while the
// tab is hidden / screen is locked. When the user comes back, all
// pending `setInterval` polls fire at once → many parallel API calls
// → if the JWT has expired during the freeze, each one independently
// returns 401 and the interceptor below would queue N redirects to
// `/login` (and remove the token N times). The token removal is
// idempotent, but the duplicated `window.location.href` assignments
// create flicker and racy navigation.
//
// `redirectingTo401` ensures that at most ONE redirect is dispatched
// per page-life. Subsequent 401s during the redirect window still
// propagate the rejection to callers (so component-level error
// handling still runs) but no longer touch `window.location`.
let redirectingTo401 = false;

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      // Always wipe the local credentials (idempotent — safe to run
      // multiple times in parallel). This is the existing behavior;
      // unchanged.
      localStorage.removeItem("sdrive_token");
      localStorage.removeItem("sdrive_user");

      // Don't auto-redirect from public pages (landing, request form).
      // This list MUST stay in sync with the original behavior.
      const publicPaths = ["/", "/login", "/register", "/register-agency", "/request"];
      if (!publicPaths.includes(window.location.pathname) && !redirectingTo401) {
        redirectingTo401 = true;
        // Use `window.location.href` (full navigation) just like
        // before — we are NOT changing the navigation mechanism, only
        // collapsing N concurrent assignments into a single one.
        window.location.href = "/login";
      }
    }
    // CRITICAL: always propagate the rejection so component-level
    // error handlers still run (toasts, retries, UI fallbacks). The
    // debounce only suppresses the *redirect*, never the error.
    return Promise.reject(err);
  }
);

export default api;
